import React, { useState } from 'react';
import {
  Button,
  Space,
  Typography,
  Card,
  Tabs,
  Layout,
} from 'antd';
import {
  LogoutOutlined,
  SendOutlined,
  DashboardOutlined,
  MailOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  FireOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ConfigProvider, theme as antdTheme } from 'antd';
import './AdminPortal.css';
import {
  OutreachDashboard,
  OutreachCampaigns,
  OutreachLeads,
  OutreachSend,
  OutreachSenderAccounts,
  OutreachWarmUp,
} from '../modules/outreach/pages';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Header, Content } = Layout;

const OutreachPortal: React.FC = () => {
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // Tab persistence
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('outreach_active_tab') || 'dashboard';
  });

  // Update sessionStorage when tab changes
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    sessionStorage.setItem('outreach_active_tab', key);
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorBgContainer: isDark ? '#141414' : '#ffffff',
          colorBgElevated: isDark ? '#1f1f1f' : '#ffffff',
          colorBgLayout: isDark ? '#000000' : '#f5f7fa',
          colorBorder: isDark ? '#434343' : '#d9d9d9',
          colorText: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
          colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
        },
      }}
    >
      <div className="admin-portal">
        <Layout className="admin-layout">
        <Header className="admin-header">
          <div className="admin-logo-section">
            <img 
              src={process.env.PUBLIC_URL + '/sns-focus-logo.png'} 
              alt="SNS Intelligence" 
            />
            <Title level={3} className="admin-title">Outreach</Title>
          </div>
          <Space size="middle">
            <Button
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? 'Light' : 'Dark'}
            </Button>
            <Button
              onClick={() => navigate('/app')}
              type="primary"
              className="dashboard-btn"
            >
              Open Portal
            </Button>
            <Button
              onClick={() => navigate('/admin')}
              type="default"
            >
              Admin Center
            </Button>
            <Button
              icon={<LogoutOutlined />}
              onClick={logout}
              danger
            >
              Logout
            </Button>
          </Space>
        </Header>

        <Content className="admin-content">
          <Card className="admin-card">
            <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
              <TabPane tab={<span><DashboardOutlined />Dashboard</span>} key="dashboard">
                <OutreachDashboard />
              </TabPane>
              <TabPane tab={<span><MailOutlined />Campaigns</span>} key="campaigns">
                <OutreachCampaigns />
              </TabPane>
              <TabPane tab={<span><TeamOutlined />Leads</span>} key="leads">
                <OutreachLeads />
              </TabPane>
              <TabPane tab={<span><SendOutlined />Send</span>} key="send">
                <OutreachSend />
              </TabPane>
              <TabPane tab={<span><UserSwitchOutlined />Sender Accounts</span>} key="sender-accounts">
                <OutreachSenderAccounts />
              </TabPane>
              <TabPane tab={<span><FireOutlined />Warm Up</span>} key="warmup">
                <OutreachWarmUp />
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>
    </div>
    </ConfigProvider>
  );
};

export default OutreachPortal;
